import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from 'framer-motion';
import { easeOut, spring } from '../../utils/motion';
import type { SwipeDirection } from '../../types/marketplace';
export interface SwipeCardHandle {
  swipe: (dir: SwipeDirection) => void;
}
export interface SwipeStamps {
  right: string;
  left: string;
  up?: string;
}
interface SwipeCardProps {
  children: React.ReactNode;
  onSwipe: (dir: SwipeDirection) => void;
  stamps: SwipeStamps;
  allowUp?: boolean;
  depth: number;
}
export const SwipeCard = forwardRef<SwipeCardHandle, SwipeCardProps>(function SwipeCard({
  children,
  onSwipe,
  stamps,
  allowUp,
  depth
}, ref) {
  const reduce = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-260, 260], reduce ? [0, 0] : [-12, 12]);
  const rightOpacity = useTransform(x, [24, 110], [0, 1]);
  const leftOpacity = useTransform(x, [-110, -24], [1, 0]);
  const upOpacity = useTransform(y, [-120, -30], [1, 0]);
  const dragged = useRef(false);
  const leaving = useRef(false);
  const isTop = depth === 0;
  const fling = (dir: SwipeDirection) => {
    if (leaving.current) return;
    leaving.current = true;
    const width = typeof window === 'undefined' ? 900 : window.innerWidth;
    const opts = {
      duration: reduce ? 0.15 : 0.28,
      ease: easeOut
    };
    if (dir === 'up') {
      animate(y, -1000, opts);
    } else {
      animate(x, dir === 'right' ? width : -width, opts);
      animate(y, y.get() + 40, opts);
    }
    setTimeout(() => onSwipe(dir), reduce ? 140 : 230);
  };
  useImperativeHandle(ref, () => ({
    swipe: fling
  }));
  return <motion.div className="absolute inset-0" style={{
    zIndex: 10 - depth
  }} initial={{
    scale: 0.9,
    y: 36,
    opacity: 0
  }} animate={{
    scale: 1 - depth * 0.04,
    y: depth * 14,
    opacity: depth > 2 ? 0 : 1
  }} transition={spring.smooth} data-id="element-751" data-mp="1rx2q1u2pq7e2">
      <motion.div className={isTop ? 'h-full w-full cursor-grab touch-none active:cursor-grabbing' : 'pointer-events-none h-full w-full'} style={{
      x,
      y,
      rotate
    }} drag={isTop ? allowUp ? true : 'x' : false} onDragStart={() => {
      dragged.current = true;
    }} onDragEnd={(_, info) => {
      const {
        offset,
        velocity
      } = info;
      if (offset.x > 110 || velocity.x > 650) fling('right');else if (offset.x < -110 || velocity.x < -650) fling('left');else if (allowUp && (offset.y < -120 || velocity.y < -700)) fling('up');else {
        animate(x, 0, spring.bouncy);
        animate(y, 0, spring.bouncy);
      }
      setTimeout(() => {
        dragged.current = false;
      }, 60);
    }} onClickCapture={e => {
      if (dragged.current) {
        e.stopPropagation();
        e.preventDefault();
      }
    }} aria-hidden={!isTop} data-id="element-752" data-mp="hqya5a84yf3n">
        {children}
        {isTop && <>
            <motion.span style={{
          opacity: rightOpacity
        }} className="pointer-events-none absolute left-5 top-16 z-20 -rotate-12 rounded-sm border-[3px] border-accent bg-accent px-3 py-1 text-2xl font-bold uppercase tracking-wide text-on-accent" data-id="element-753" data-mp="38vwaqcskigz8">
              {stamps.right}
            </motion.span>
            <motion.span style={{
          opacity: leftOpacity
        }} className="pointer-events-none absolute right-5 top-16 z-20 rotate-12 rounded-sm border-[3px] border-white bg-black/70 px-3 py-1 text-2xl font-bold uppercase tracking-wide text-white" data-id="element-754" data-mp="3rnxd3p4jwpde">
              {stamps.left}
            </motion.span>
            {allowUp && stamps.up && <motion.span style={{
          opacity: upOpacity
        }} className="pointer-events-none absolute left-1/2 top-1/3 z-20 -translate-x-1/2 rounded-sm border-[3px] border-brand bg-brand px-3 py-1 text-2xl font-bold uppercase tracking-wide text-on-brand" data-id="element-755" data-mp="278lms6f0cagw">
                {stamps.up}
              </motion.span>}
          </>}
      </motion.div>
    </motion.div>;
});